import React, { useState, useMemo } from "react";
import {
  Users,
  Eye,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Filter,
  ChevronRight as ChevronRightIcon,
  CheckCircle2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Consultation,
  useConsultationsState,
} from "@/lib/dashboard/consultationsData";
import { ConsultationSummaryModal } from "./ConsultationSummaryModal";
import ConsultationDetails from "./ConsultationDetails";

interface PreviousConsultationsProps {
  consultations: Consultation[];
  isDarkMode: boolean;
  onSelectConsultation: (consultation: Consultation) => void;
}
// Helper function to get action badge colors
const getActionColor = (actionType: string): string => {
  switch (actionType.toLowerCase()) {
    case "prescribemedication":
    case "prescription":
      return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800";
    case "recordvitals":
    case "vitals":
      return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800";
    case "sendreferralletter":
    case "referral":
      return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800";
    case "vaccine":
      return "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800";
    case "payment":
      return "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
  }
};

// Helper function to get display name for action types
const getActionDisplayName = (actionType: string): string => {
  switch (actionType) {
    case "RecordVitals":
      return "Vitals";
    case "PrescribeMedication":
      return "Prescription";
    case "SendReferralLetter":
      return "Referral";
    case "CreateTreatmentPlan":
      return "Treatment Plan";
    default:
      return actionType;
  }
};
const safeJsonParse = (value: any) => {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();

  // JSON MUST start like this
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value; // plain text → DO NOT PARSE
  }

  try {
    return JSON.parse(trimmed);
  } catch (err) {
    console.warn("Invalid JSON skipped:", value);
    return value;
  }
};

// Process actions data to extract performed status
const processActionsData = (actions: any[]) => {
  return (
    actions?.map((action) => {
      const isPerformed = action.isPerformed === true;
      const actiontitle = action.actionName || action.type || "Unknown";
      const actionName =
        action.actionName === "CreatePrescriptionDraft"
          ? "Prescription draft"
          : action.actionName === "CreateVaccineDraft"
            ? "Vaccination draft"
            : actiontitle;
      return {
        ...action,
        isPerformed,
        displayName: getActionDisplayName(actionName),
        type: actionName,
        // Extract additional metadata from payload if needed
        payload:
          typeof action.payload === "string"
            ? safeJsonParse(action.payload)
            : action.payload,
      };
    }) || []
  );
};

// Process consultations to include performed status
const processConsultations = (consultations: Consultation[]) => {
  return consultations.map((consultation) => ({
    ...consultation,
    actions: processActionsData(consultation.actions),
  }));
};

// Action Badge Component with performed status
interface ActionBadgeProps {
  action: any;
  isDarkMode: boolean;
}

function ActionBadge({ action, isDarkMode }: ActionBadgeProps) {
  const displayName =
    action.displayName ||
    getActionDisplayName(action.actionName || action.type);
  const isPerformed = action.isPerformed;

  return (
    <Badge
      variant="secondary"
      className={`text-xs px-2 py-1 flex items-center gap-1.5 ${getActionColor(
        action.actionName || action.type,
      )}`}
    >
      {isPerformed ? (
        <CheckCircle2 className="flex-shrink-0 w-3 h-3" />
      ) : (
        <div className="flex-shrink-0 w-3 h-3 border-2 border-current rounded-full opacity-50" />
      )}
      <span>{displayName}</span>
    </Badge>
  );
}

interface ActionDetailsProps {
  action: any;
  isDarkMode: boolean;
}

function ActionDetails({ action, isDarkMode }: ActionDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div
      className={`text-xs mt-1 p-3 rounded-lg border transition-colors duration-200 ${
        isDarkMode
          ? "bg-gray-800/50 border-gray-600 hover:bg-gray-700/50"
          : "bg-gray-50 border-gray-200 hover:bg-white"
      }`}
    >
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center flex-1 min-w-0 gap-3">
          <ActionBadge action={action} isDarkMode={isDarkMode} />
          <Badge
            variant={action.isPerformed ? "default" : "secondary"}
            className={`text-xs whitespace-nowrap ${
              action.isPerformed
                ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300"
                : "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300"
            }`}
          >
            {action.isPerformed ? "Completed" : "Pending"}
          </Badge>
        </div>
        <ChevronRightIcon
          className={`w-4 h-4 transition-transform duration-300 flex-shrink-0 ${
            isExpanded ? "rotate-90" : ""
          } ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        />
      </div>
      {isExpanded && (
        <div
          className={`mt-3 pt-3 border-t ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
        >
          {action.payload && (
            <div
              className={`p-3 rounded border ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              {renderEnhancedPayloadDetails(
                action.payload,
                action.actionName,
                isDarkMode,
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function renderEnhancedPayloadDetails(
  payload: any,
  actionName: string,
  isDarkMode: boolean,
) {
  if (!payload) {
    return (
      <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
        No details available
      </div>
    );
  }

  try {
    const payloadData =
      typeof payload === "string" ? JSON.parse(payload) : payload;
    switch (actionName) {
      case "PrescribeMedication":
        return renderPrescriptionPayload(payloadData, isDarkMode);
      case "RecordVitals":
        return renderVitalsPayload(payloadData, isDarkMode);
      case "SendReferralLetter":
        return renderReferralPayload(payloadData, isDarkMode);
      case "Vaccinations":
        return renderVaccinationPayload(payloadData, isDarkMode);
      case "Signature":
        return renderSignaturePayload(payloadData, isDarkMode);
      case "Image":
        return renderImagePayload(payloadData, isDarkMode);
      case "CreatePrescriptionDraft":
        return renderPrescriptionDraftPayload(payloadData, isDarkMode);
      case "CreateVaccineDraft":
        return renderVaccinationDraftPayload(payloadData, isDarkMode);
      case "CreateTask":
        return renderTasksPayload(payloadData, isDarkMode);
      case "CreateTreatmentPlan":
        return renderTreatmentPlanPayload(payloadData, isDarkMode);
      default:
        return renderGenericPayload(payloadData, isDarkMode);
    }
  } catch (error) {
    return (
      <div className="text-xs text-red-500">Error parsing payload data</div>
    );
  }
}

function renderPrescriptionPayload(payload: any, isDarkMode: boolean) {
  const prescriptionDetails = payload.prescriptionDetails || payload;
  const medications =
    prescriptionDetails.drugs || prescriptionDetails.prescriptionsDetails || [];

  return (
    <div className="space-y-3">
      {/* Medications List */}
      {medications.length > 0 ? (
        <div>
          <div
            className={`font-medium mb-2 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Medications
          </div>
          <div className="space-y-2">
            {medications.map((drug: any, index: number) => (
              <div
                key={
                  drug.prescriptionItemId ||
                  drug.prescribedItemId ||
                  drug.id ||
                  index
                }
                className={`p-3 rounded border ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-800"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div
                  className={`font-medium ${
                    isDarkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {drug.drugName?.trim() ||
                    drug.value?.trim() ||
                    drug.name?.trim() ||
                    `Medication ${index + 1}`}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  {drug.quantity && (
                    <div>
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        Quantity:
                      </span>
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        {" "}
                        {drug.quantity}
                      </span>
                    </div>
                  )}
                  {drug.dose && (
                    <div>
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        Dosage:
                      </span>
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        {" "}
                        {drug.dose}
                      </span>
                    </div>
                  )}
                  {drug.note && (
                    <div className="col-span-2">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        Instructions:
                      </span>
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        {" "}
                        {drug.note}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          No prescription details available
        </div>
      )}
    </div>
  );
}

function renderPrescriptionDraftPayload(payload: any, isDarkMode: boolean) {
  const prescriptionDetails = payload.prescriptionDetails || payload;

  const medications =
    prescriptionDetails.prescriptionItems ||
    prescriptionDetails.drugs ||
    prescriptionDetails.prescriptionsDetails ||
    prescriptionDetails.calculatorResults ||
    [];

  return (
    <div className="space-y-3">
      {medications.length > 0 ? (
        <div>
          <div
            className={`font-medium mb-2 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Medications
          </div>

          <div className="space-y-2">
            {medications.map((drug: any, index: number) => {
              const name =
                drug.drugName?.trim() ||
                drug.value?.trim() ||
                drug.medication?.trim() ||
                drug.name?.trim() ||
                `Medication ${index + 1}`;

              const dosage = drug.dose || drug.dosage;
              const notes = drug.note || drug.notes;

              return (
                <div
                  key={
                    drug.prescriptionItemId ||
                    drug.prescribedItemId ||
                    drug.id ||
                    index
                  }
                  className={`p-3 rounded border ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {name}
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    {drug.quantity && (
                      <div>
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }
                        >
                          Quantity:
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }
                        >
                          {" "}
                          {drug.quantity}
                        </span>
                      </div>
                    )}

                    {dosage && (
                      <div>
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }
                        >
                          Dosage:
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }
                        >
                          {" "}
                          {dosage}
                        </span>
                      </div>
                    )}

                    {drug.duration && (
                      <div>
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }
                        >
                          Duration:
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }
                        >
                          {" "}
                          {drug.duration}
                        </span>
                      </div>
                    )}

                    {notes && (
                      <div className="col-span-2">
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }
                        >
                          Instructions:
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }
                        >
                          {" "}
                          {notes}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          No prescription details available
        </div>
      )}
    </div>
  );
}

function renderVitalsPayload(payload: any, isDarkMode: boolean) {
  const vitals = payload.vitalsDetails || payload;

  const vitalFields = [
    {
      key: "weight",
      label: "Weight",
      format: (v: any) => {
        if (!v || !v.Value || v.Value === "" || v.Value === "--") return null;
        return `${v.Value} ${v.Unit || ""}`.trim();
      },
    },
    {
      key: "height",
      label: "Height",
      format: (v: any) => {
        if (!v || !v.Value || v.Value === "" || v.Value === "--") return null;
        return `${v.Value} ${v.Unit || ""}`.trim();
      },
    },
    {
      key: "heartRate",
      label: "Heart Rate",
      format: (v: any) => {
        if (!v || !v.Value || v.Value === "" || v.Value === "--") return null;
        return `${v.Value} ${v.Unit || ""}`.trim();
      },
    },
    {
      key: "temperature",
      label: "Temperature",
      format: (v: any) => {
        if (!v || !v.Value || v.Value === "" || v.Value === "--") return null;
        return `${v.Value} ${v.Unit || ""}`.trim();
      },
    },
    {
      key: "respiratoryRate",
      label: "Respiratory Rate",
      format: (v: any) => {
        if (!v || !v.Value || v.Value === "" || v.Value === "--") return null;
        return `${v.Value} ${v.Unit || ""}`.trim();
      },
    },
    {
      key: "bloodPressure",
      label: "Blood Pressure",
      format: (v: any) => {
        if (!v) return null;
        const systolic = v.Systolic?.Value;
        const diastolic = v.Diastolic?.Value;
        if (!systolic && !diastolic) return null;
        if (
          systolic === "" ||
          systolic === "--" ||
          diastolic === "" ||
          diastolic === "--"
        )
          return null;

        const sysValue = systolic || "--";
        const diaValue = diastolic || "--";
        const unit = v.Systolic?.Unit || v.Diastolic?.Unit || "";
        return `${sysValue}/${diaValue} ${unit}`.trim();
      },
    },
    {
      key: "bmi",
      label: "BMI",
      format: (v: any) => {
        if (!v || !v.Value || v.Value === "" || v.Value === "--") return null;
        return `${v.Value} ${v.Unit || ""}`.trim();
      },
    },
  ];

  // Filter out fields that are not recorded
  const recordedVitals = vitalFields.filter((field) => {
    const value = vitals[field.key];
    const formattedValue = field.format(value);
    return (
      formattedValue !== null &&
      formattedValue !== undefined &&
      formattedValue !== ""
    );
  });

  if (recordedVitals.length === 0) {
    return (
      <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
        No vitals recorded
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {recordedVitals.map((field) => {
        const value = vitals[field.key];
        const formattedValue = field.format(value);

        return (
          <div
            key={field.key}
            className={`p-2 rounded border text-xs ${
              isDarkMode
                ? "border-gray-600 bg-gray-800"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
              {field.label}
            </div>
            <div
              className={`font-medium ${
                isDarkMode ? "text-gray-200" : "text-gray-800"
              }`}
            >
              {formattedValue}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function renderReferralPayload(payload: any, isDarkMode: boolean) {
  const referral = JSON.parse(payload.payload).letterContent;
  return (
    <div className="space-y-2">
      {referral.content ? (
        <div>
          <div
            className={`font-medium mb-1 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Referral Content:
          </div>
          <div
            className={`p-2 rounded border max-h-32 overflow-y-auto text-xs ${
              isDarkMode
                ? "border-gray-600 bg-gray-800"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            {referral.content}
          </div>
        </div>
      ) : (
        <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          No referral content available
        </div>
      )}
    </div>
  );
}
function renderVaccinationPayload(payload: any, isDarkMode: boolean) {
  const vaccination = payload.vaccinationDetails;

  const vaccines = Array.isArray(vaccination)
    ? vaccination
    : vaccination?.vaccinations || [];

  if (vaccines.length === 0) {
    return (
      <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
        No vaccination details recorded
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {vaccines.map((vaccine: any, index: number) => (
        <div key={vaccine.id || index} className="space-y-2">
          <div
            className={`font-medium ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Vaccine {index + 1}
          </div>
          <div
            className={`p-3 rounded border ${
              isDarkMode
                ? "border-gray-600 bg-gray-800"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="grid grid-cols-2 gap-3">
              {/* Vaccine Name */}
              {vaccine.value?.vaccineName && (
                <div
                  className={`p-2 rounded border text-xs ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Vaccine Name
                  </div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {vaccine.value.vaccineName}
                  </div>
                </div>
              )}

              {/* Dose */}
              {vaccine.dose && (
                <div
                  className={`p-2 rounded border text-xs ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Dose
                  </div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {vaccine.dose}
                  </div>
                </div>
              )}

              {/* Schedule */}
              {vaccine.schedule && (
                <div
                  className={`p-2 rounded border text-xs ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Schedule
                  </div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {vaccine.schedule}
                  </div>
                </div>
              )}

              {/* Site */}
              {vaccine.site && (
                <div
                  className={`p-2 rounded border text-xs ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Administration Site
                  </div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {vaccine.site}
                  </div>
                </div>
              )}

              {/* Manufacturer */}
              {vaccine.manufacturer && (
                <div
                  className={`p-2 rounded border text-xs ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Manufacturer
                  </div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {vaccine.manufacturer}
                  </div>
                </div>
              )}

              {/* Batch Number */}
              {vaccine.batchNumber && (
                <div
                  className={`p-2 rounded border text-xs ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Batch Number
                  </div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {vaccine.batchNumber}
                  </div>
                </div>
              )}

              {/* Expiry Date */}
              {vaccine.expiryDate && (
                <div
                  className={`p-2 rounded border text-xs ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  >
                    Expiry Date
                  </div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {vaccine.expiryDate}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function renderVaccinationDraftPayload(payload: any, isDarkMode: boolean) {
  const vaccinationDetails = payload.vaccinationDetails || payload;

  const vaccines =
    vaccinationDetails.vaccineItems ||
    (Array.isArray(vaccinationDetails)
      ? vaccinationDetails
      : vaccinationDetails?.vaccinations) ||
    vaccinationDetails.calculatorResults ||
    [];

  if (vaccines.length === 0) {
    return (
      <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
        No vaccination details recorded
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {vaccines.map((vaccine: any, index: number) => {
        const name =
          vaccine.value?.vaccineName ||
          vaccine.vaccineName ||
          vaccine.medication ||
          `Vaccine ${index + 1}`;

        const dosage = vaccine.dose || vaccine.dosage;
        const schedule = vaccine.schedule || vaccine.duration;
        const notes = vaccine.note || vaccine.notes;

        return (
          <div
            key={vaccine.id || vaccine.prescribedItemId || index}
            className="space-y-2"
          >
            <div
              className={`font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {name}
            </div>

            <div
              className={`p-3 rounded border ${
                isDarkMode
                  ? "border-gray-600 bg-gray-800"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="grid grid-cols-2 gap-3 text-xs">
                {/* Dose */}
                {dosage && (
                  <div>
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Dose:
                    </span>
                    <span
                      className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                    >
                      {" "}
                      {dosage}
                    </span>
                  </div>
                )}

                {/* Schedule */}
                {schedule && (
                  <div>
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Schedule:
                    </span>
                    <span
                      className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                    >
                      {" "}
                      {schedule}
                    </span>
                  </div>
                )}

                {/* Route */}
                {vaccine.route && (
                  <div>
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Route:
                    </span>
                    <span
                      className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                    >
                      {" "}
                      {vaccine.route}
                    </span>
                  </div>
                )}

                {/* Site */}
                {vaccine.site && (
                  <div>
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Site:
                    </span>
                    <span
                      className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                    >
                      {" "}
                      {vaccine.site}
                    </span>
                  </div>
                )}

                {/* Quantity (from calculatorResults) */}
                {vaccine.quantity && (
                  <div>
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Quantity:
                    </span>
                    <span
                      className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                    >
                      {" "}
                      {vaccine.quantity}
                    </span>
                  </div>
                )}

                {/* Notes */}
                {notes && (
                  <div className="col-span-2">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Notes:
                    </span>
                    <span
                      className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                    >
                      {" "}
                      {notes}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function renderSignaturePayload(payload: any, isDarkMode: boolean) {
  const filteredPayload = filterTechnicalFields(payload);

  if (Object.keys(filteredPayload).length === 0) {
    return (
      <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
        No additional details available
      </div>
    );
  }

  const rows: { label: string; value: any }[] = [];

  Object.entries(filteredPayload).forEach(([key, value]) => {
    if (key === "patientRelationStatus" && value) {
      rows.push({ label: "Name", value: value.name });
      rows.push({ label: "Relationship", value: value.relationship });
    } else if (key === "signedAt") {
      rows.push({
        label: "Signed At",
        value: new Date(value as string).toLocaleString("en-GB"),
      });
    } else {
      rows.push({
        label: formatKey(key),
        value,
      });
    }
  });

  return (
    <div className="space-y-2">
      {rows.map((row, index) => (
        <div key={index} className="grid grid-cols-3 gap-2 text-xs">
          <span
            className={`capitalize ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {row.label}:
          </span>

          <span
            className={`col-span-2 break-all ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderImagePayload(data: any, isDarkMode: boolean) {
  const { payload, recordedAt } = data;

  if (!payload) {
    return (
      <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
        No image details available
      </div>
    );
  }

  const rows = [
    { label: "File Name", value: payload.title },
    { label: "Description", value: payload.description },
    { label: "File Size", value: payload.size },
    { label: "File Type", value: payload.contentType },
    {
      label: "Uploaded At",
      value: recordedAt
        ? new Date(recordedAt).toISOString().split("T")[0]
        : "N/A",
    },
  ];

  return (
    <div className="space-y-2">
      {rows.map((row, index) => (
        <div key={index} className="grid grid-cols-3 gap-2 text-xs">
          <span
            className={`capitalize ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {row.label}:
          </span>

          <span
            className={`col-span-2 break-all ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderTasksPayload(payload: any, isDarkMode: boolean) {
  const tasks = payload?.tasks || [];

  if (!Array.isArray(tasks) || tasks.length === 0) {
    return (
      <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
        No tasks recorded
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {tasks.map((task: any, index: number) => (
        <div
          key={task.id || index}
          className={`p-2 rounded ${
            isDarkMode
              ? "bg-gray-700/50 border border-gray-600"
              : "bg-gray-50 border border-gray-200"
          }`}
        >
          <div className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
            {task.label || `Task ${index + 1}`}
          </div>
        </div>
      ))}
    </div>
  );
}

function renderGenericPayload(payload: any, isDarkMode: boolean) {
  // Filter out technical fields and format booking data properly
  const filteredPayload = filterTechnicalFields(payload);
  if (Object.keys(filteredPayload).length === 0) {
    return (
      <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
        No additional details available
      </div>
    );
  }

  // Check if this is booking data and render specially
  if (isBookingData(payload)) {
    return renderBookingPayload(payload, isDarkMode);
  }

  return (
    <div className="space-y-2">
      {Object.entries(filteredPayload).map(([key, value]) => (
        <div key={key} className="grid grid-cols-3 gap-2 text-xs">
          <span
            className={`capitalize ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {formatKey(key)}:
          </span>
          <span
            className={`col-span-2 break-all ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            {formatValue(key, value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// Helper function to filter out technical fields
function filterTechnicalFields(payload: any): Record<string, any> {
  const technicalFields = [
    "actionID",
    "actionName",
    "actionNumber",
    "actionStatus",
    "isPerformed",
    "displayName",
    "type",
    "image",
    "consultationID",
    "cls",
    "recurrenceRule",
    "exceptionDates",
    "allDay",
    "scheduleEventType",
    "confirmationMethod",
    "filecontent",
    "patientid",
    "consultationid",
    "status",
    "isvideo",
    "size",
  ];

  return Object.keys(payload).reduce(
    (acc, key) => {
      if (!technicalFields.includes(key.toLowerCase())) {
        acc[key] = payload[key];
      }
      return acc;
    },
    {} as Record<string, any>,
  );
}

// Check if payload contains booking data (but not treatment plans, which also have startDate)
function isBookingData(payload: any): boolean {
  if (payload.resolvedDrugId || payload.pgdIdAtInitiation || payload.episodeOrigin) {
    return false;
  }
  const bookingIndicators = [
    "startDate",
    "endDate",
    "duration",
    "resourceId",
    "serviceID",
  ];
  return bookingIndicators.some((indicator) =>
    payload.hasOwnProperty(indicator),
  );
}

// Render booking data in a structured way
function renderBookingPayload(payload: any, isDarkMode: boolean) {
  const booking = payload;

  return (
    <div className="space-y-3">
      {/* Patient Information */}
      {(booking.name || booking.email || booking.mobile) && (
        <div>
          <div
            className={`font-medium mb-2 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Patient Information
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {booking.name && (
              <div>
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                >
                  Name:
                </span>
                <div className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                  {booking.name}
                </div>
              </div>
            )}
            {booking.email && (
              <div>
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                >
                  Email:
                </span>
                <div className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                  {booking.email}
                </div>
              </div>
            )}
            {booking.mobile && (
              <div>
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                >
                  Mobile:
                </span>
                <div className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                  {booking.mobile}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Appointment Details */}
      <div>
        <div
          className={`font-medium mb-2 ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
        >
          Appointment Details
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          {booking.startDate && (
            <div>
              <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Date:
              </span>
              <div className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                {formatDate(booking.startDate)}
              </div>
            </div>
          )}
          {booking.startDate && (
            <div>
              <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Time:
              </span>
              <div className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                {formatTime(booking.startDate)} - {formatTime(booking.endDate)}
              </div>
            </div>
          )}
          {booking.duration && (
            <div>
              <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Duration:
              </span>
              <div className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                {booking.duration} {booking.durationUnit || "minutes"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function renderTreatmentPlanPayload(payload: any, isDarkMode: boolean) {
  const label = (text: string) => (
    <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>{text}:</span>
  );
  const value = (text: string) => (
    <div className={isDarkMode ? "text-gray-300" : "text-gray-700"}>{text || "—"}</div>
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 text-xs">
        {payload.name && (
          <div className="col-span-2">{label("Plan")} {value(payload.name)}</div>
        )}
        {payload.status && (
          <div>{label("Status")} {value(payload.status)}</div>
        )}
        {payload.startDate && (
          <div>{label("Start Date")} {value(formatDate(payload.startDate))}</div>
        )}
        {payload.currentDose && (
          <div>{label("Current Dose")} {value(payload.currentDose)}</div>
        )}
        {payload.resolvedDrugId && (
          <div>{label("Drug")} {value(payload.resolvedDrugId.charAt(0).toUpperCase() + payload.resolvedDrugId.slice(1))}</div>
        )}
        {payload.baselineWeightKg != null && (
          <div>{label("Baseline Weight")} {value(`${payload.baselineWeightKg} kg`)}</div>
        )}
        {payload.baselineBmi != null && (
          <div>{label("Baseline BMI")} {value(`${payload.baselineBmi} kg/m²`)}</div>
        )}
        {payload.nextSupplyDueDate && (
          <div>{label("Next Supply Due")} {value(formatDate(payload.nextSupplyDueDate))}</div>
        )}
      </div>
    </div>
  );
}

// Helper functions for formatting
function formatKey(key: string): string {
  const keyMap: Record<string, string> = {
    id: "ID",
    patientID: "Patient ID",
    startDate: "Start Date",
    endDate: "End Date",
    durationUnit: "Duration Unit",
    resourceId: "Resource ID",
    serviceID: "Service ID",
    RoomId: "Room ID",
  };

  return (
    keyMap[key] ||
    key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
  );
}

function formatValue(key: string, value: any): string {
  if (
    typeof value === "string" &&
    value.length > 0 &&
    (key.toLowerCase().includes("date") || key.toLowerCase().endsWith("at"))
  ) {
    return formatDate(value);
  }
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatDateTime(dateString: string): string {
  if (!dateString || typeof dateString !== "string") return String(dateString ?? "");
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleString("en-GB");
}

function formatDate(dateString: string): string {
  if (!dateString || typeof dateString !== "string") return String(dateString ?? "");
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString();
}

function formatTime(dateString: string): string {
  if (!dateString || typeof dateString !== "string") return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function PreviousConsultations({
  isDarkMode,
  onSelectConsultation,
}: PreviousConsultationsProps) {
  const [selectedConsultations, setSelectedConsultations] = useState<string[]>(
    [],
  );
  const {
    consultationsUI,
    isConsultationPointsLoading,
    summarizeConsultationSummaries,
  } = useConsultationsState();
  // Process consultations to include performed status
  const consultations = useMemo(() => {
    return processConsultations(consultationsUI || []);
  }, [consultationsUI]);

  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [expandedConsultation, setExpandedConsultation] = useState<
    string | null
  >(null);
  const itemsPerPage = 5;

  // Helper function to parse consultation date with error handling
  const parseConsultationDate = (dateStr: string): Date | null => {
    try {
      // Handle DD/MM/YY format (e.g., "15/01/24")
      const [day, month, year] = dateStr.split("/");
      if (!day || !month || !year) return null;

      // Convert 2-digit year to 4-digit (assuming 2000s)
      const fullYear = year.length === 2 ? `20${year}` : year;

      // Create date object (month is 0-indexed)
      const date = new Date(
        parseInt(fullYear),
        parseInt(month) - 1,
        parseInt(day),
      );

      // Validate the date
      if (isNaN(date.getTime())) return null;

      return date;
    } catch {
      return null;
    }
  };

  // Pre-compute consultation dates for better performance
  const consultationsWithDates = useMemo(() => {
    return consultations.map((consultation) => ({
      ...consultation,
      parsedDate: parseConsultationDate(consultation.date),
    }));
  }, [consultations]);

  // Filter consultations based on selected filters
  const filteredConsultations = useMemo(() => {
    let filtered = consultationsWithDates;

    // Apply date filter
    if (dateFilter !== "all") {
      const now = new Date();
      let filterDate: Date;

      switch (dateFilter) {
        case "today":
          filterDate = new Date();
          filterDate.setHours(0, 0, 0, 0);
          break;
        case "7days":
          filterDate = new Date();
          filterDate.setDate(now.getDate() - 7);
          filterDate.setHours(0, 0, 0, 0);
          break;
        case "30days":
          filterDate = new Date();
          filterDate.setDate(now.getDate() - 30);
          filterDate.setHours(0, 0, 0, 0);
          break;
        case "90days":
          filterDate = new Date();
          filterDate.setDate(now.getDate() - 90);
          filterDate.setHours(0, 0, 0, 0);
          break;
        default:
          filterDate = new Date(0); // Fallback
      }

      filtered = filtered.filter((consultation) => {
        if (!consultation.parsedDate) return false;
        return consultation.parsedDate >= filterDate;
      });
    }

    // Apply consultation type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(
        (consultation) => consultation.type === typeFilter,
      );
    }
    filtered = filtered.sort((a, b) => {
      const dateA = new Date(a.createdOn || 0).getTime();
      const dateB = new Date(b.createdOn || 0).getTime();
      return dateB - dateA;
    });
    // Remove the parsedDate property before returning
    return filtered.map(({ parsedDate, ...consultation }) => consultation);
  }, [consultationsWithDates, dateFilter, typeFilter]);

  // Calculate pagination based on filtered results
  const totalPages = Math.ceil(filteredConsultations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentConsultations = filteredConsultations.slice(
    startIndex,
    endIndex,
  );

  const handleConsultationSelect = React.useCallback(
    (consultationId: string, checked: boolean) => {
      if (checked) {
        setSelectedConsultations((prev) => [...prev, consultationId]);
      } else {
        setSelectedConsultations((prev) =>
          prev.filter((id) => id !== consultationId),
        );
      }
    },
    [],
  );

  const handleSelectAll = React.useCallback(
    (checked: boolean | "indeterminate") => {
      if (checked === true || checked === "indeterminate") {
        setSelectedConsultations(
          currentConsultations.map((c) => c.consultationId),
        );
      } else {
        setSelectedConsultations([]);
      }
    },
    [currentConsultations],
  );

  // Reset selections when changing pages
  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedConsultations([]);
    setExpandedConsultation(null);
  }, []);

  // Reset pagination and selections when filters change
  const handleDateFilterChange = React.useCallback((value: string) => {
    setDateFilter(value);
    setCurrentPage(1);
    setSelectedConsultations([]);
    setExpandedConsultation(null);
  }, []);

  const handleTypeFilterChange = React.useCallback((value: string) => {
    setTypeFilter(value);
    setCurrentPage(1);
    setSelectedConsultations([]);
    setExpandedConsultation(null);
  }, []);

  // Memoized clear filters function
  const handleClearFilters = React.useCallback(() => {
    setDateFilter("all");
    setTypeFilter("all");
    setCurrentPage(1);
    setSelectedConsultations([]);
    setExpandedConsultation(null);
  }, []);

  const handleSummarise = () => {
    summarizeConsultationSummaries({
      consultationIds: selectedConsultations,
    }).then((res) => {
      setIsSummaryModalOpen(true);
    });
  };

  const handleCloseSummaryModal = React.useCallback(() => {
    setIsSummaryModalOpen(false);
  }, []);

  const handleToggleExpand = React.useCallback((consultationId: string) => {
    setExpandedConsultation((prev) =>
      prev === consultationId ? null : consultationId,
    );
  }, []);

  // Get selected consultation data for the modal
  const selectedConsultationData = React.useMemo(() => {
    return filteredConsultations.filter((c) =>
      selectedConsultations.includes(c.consultationId),
    );
  }, [filteredConsultations, selectedConsultations]);

  // Calculate performed actions statistics
  const consultationStats = React.useMemo(() => {
    return currentConsultations.map((consultation) => {
      const totalActions = consultation.actions.length;
      const performedActions = consultation.actions.filter(
        (action) => action.isPerformed,
      ).length;
      return {
        id: consultation.consultationId,
        total: totalActions,
        performed: performedActions,
        percentage:
          totalActions > 0 ? (performedActions / totalActions) * 100 : 0,
      };
    });
  }, [currentConsultations]);

  const isAllSelected =
    currentConsultations.length > 0 &&
    selectedConsultations.length === currentConsultations.length;
  const isPartiallySelected =
    selectedConsultations.length > 0 &&
    selectedConsultations.length < currentConsultations.length;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users
            className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Previous Consultations
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {filteredConsultations.length}/{consultations.length}
          </Badge>
          {currentConsultations.length > 0 && (
            <div className="flex items-center gap-1">
              <Checkbox
                id="select-all-previous"
                checked={isPartiallySelected ? "indeterminate" : isAllSelected}
                onCheckedChange={handleSelectAll}
                className="w-4 h-4"
              />
              <label
                htmlFor="select-all-previous"
                className={`text-xs cursor-pointer transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                All
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Filter Controls */}
      <div
        className={`mb-4 p-3 rounded-lg border transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-700/50 border-gray-600"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter
              className={`w-4 h-4 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            />
            <span
              className={`text-xs font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Filters:
            </span>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2">
            <label
              className={`text-xs transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Date:
            </label>
            <Select value={dateFilter} onValueChange={handleDateFilterChange}>
              <SelectTrigger
                className={`w-32 h-7 text-xs transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                }`}
              >
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <label
              className={`text-xs transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Type:
            </label>
            <Select value={typeFilter} onValueChange={handleTypeFilterChange}>
              <SelectTrigger
                className={`w-40 h-7 text-xs transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                }`}
              >
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All Types</SelectItem>
                {consultations
                  .map((consultation) => consultation.type)
                  .filter(
                    (type, index, self) => type && self.indexOf(type) === index,
                  )
                  .map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clear Filters */}
          {(dateFilter !== "all" || typeFilter !== "all") && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleClearFilters}
              className="px-2 text-xs h-7"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {selectedConsultations.length > 0 && (
        <div
          className={`mb-4 p-3 rounded-lg border transition-colors duration-300 ${
            isDarkMode
              ? "bg-blue-900/20 border-blue-800"
              : "bg-blue-50 border-blue-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              {selectedConsultations?.length} consultation
              {selectedConsultations?.length !== 1 ? "s" : ""} selected
            </span>
            <Button
              size="sm"
              onClick={handleSummarise}
              disabled={isConsultationPointsLoading}
              className="text-white transition-all duration-300 shadow-lg bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-700 hover:via-blue-700 hover:to-cyan-700 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isConsultationPointsLoading ? (
                <>
                  <div className="w-4 h-4 mr-1 border-2 border-white rounded-full border-t-transparent animate-spin" />
                  Summarising...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1" />
                  Summarise
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {currentConsultations.length === 0 ? (
          <div
            className={`text-center py-8 transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="mb-1 text-sm font-medium">
              {filteredConsultations.length === 0 &&
              (dateFilter !== "all" || typeFilter !== "all")
                ? "No consultations match your filters"
                : "No consultations found"}
            </p>
            {filteredConsultations.length === 0 &&
              (dateFilter !== "all" || typeFilter !== "all") && (
                <p className="text-xs opacity-75">
                  Try adjusting your filters or clearing them to see more
                  results.
                </p>
              )}
          </div>
        ) : (
          currentConsultations.map((consultation, index) => {
            const isSelected = selectedConsultations.includes(
              consultation.consultationId,
            );
            const isExpanded =
              expandedConsultation === consultation.consultationId;
            const stats = consultationStats.find(
              (s) => s.id === consultation.consultationId,
            );
            return (
              <div
                key={consultation.consultationId}
                className={`group border rounded-xl transition-colors duration-300 ${
                  isDarkMode ? "border-gray-600" : "border-gray-200"
                } ${
                  isSelected
                    ? isDarkMode
                      ? "bg-blue-900/20 border-blue-700"
                      : "bg-blue-50 border-blue-300"
                    : isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600"
                      : "bg-white hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start gap-3 p-3">
                  <Checkbox
                    id={`consultation-previous-${consultation.consultationId}`}
                    checked={isSelected}
                    onCheckedChange={(checked) =>
                      handleConsultationSelect(
                        consultation.consultationId,
                        checked === true,
                      )
                    }
                    className="w-4 h-4 mt-1"
                  />
                  <div className="flex-1">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() =>
                        handleToggleExpand(consultation.consultationId)
                      }
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-xs font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {consultation.date}
                          </span>
                          <span
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {consultation?.time}
                          </span>
                          {consultation?.type && (
                            <Badge variant="outline" className="text-xs">
                              {consultation?.type}
                            </Badge>
                          )}
                          {/* Performance indicator */}
                          {stats && stats.total > 0 && (
                            <Badge
                              variant="secondary"
                              className={`text-xs ${
                                stats.percentage === 100
                                  ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300"
                                  : stats.percentage >= 50
                                    ? "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300"
                                    : "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300"
                              }`}
                            >
                              {stats.performed}/{stats.total} actions
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {consultation.actions
                            .slice(0, 3)
                            .map((action, actionIndex) => (
                              <ActionBadge
                                key={actionIndex}
                                action={action}
                                isDarkMode={isDarkMode}
                              />
                            ))}
                          {consultation.actions.length > 3 && (
                            <Badge
                              variant="secondary"
                              className="text-xs px-1.5 py-0.5"
                            >
                              +{consultation.actions.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className={`opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 ${
                            isExpanded ? "opacity-100" : ""
                          }`}
                        >
                          <Eye
                            className="w-3 h-3 text-blue-500"
                            title="Preview Consultation"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectConsultation(consultation);
                            }}
                          />
                        </div>
                        <ChevronRightIcon
                          className={`w-4 h-4 transition-transform duration-300 ${
                            isExpanded ? "rotate-90" : ""
                          } ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
                        />
                      </div>
                    </div>

                    {/* Expanded Actions Details */}
                    {isExpanded && consultation.actions.length > 0 && (
                      <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-600">
                        <div className="space-y-2">
                          {consultation.actions.map((action, actionIndex) => (
                            <ActionDetails
                              key={actionIndex}
                              action={action}
                              isDarkMode={isDarkMode}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {filteredConsultations.length > 0 && totalPages > 1 && (
        <div
          className={`mt-4 pt-3 border-t flex items-center justify-between transition-colors duration-300 ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
        >
          <div
            className={`text-xs transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Showing {startIndex + 1}-
            {Math.min(endIndex, filteredConsultations.length)} of{" "}
            {filteredConsultations.length}
            {filteredConsultations.length !== consultations.length && (
              <span
                className={`ml-1 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                (filtered from {consultations.length})
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Previous Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === 1
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-2 py-1 text-xs rounded transition-colors duration-300 ${
                  page === currentPage
                    ? isDarkMode
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}

            {/* Next Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === totalPages
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* AI Summary Modal */}
      <ConsultationSummaryModal
        isOpen={isSummaryModalOpen}
        onClose={handleCloseSummaryModal}
        consultations={selectedConsultationData}
        isDarkMode={isDarkMode}
      />
      <ConsultationDetails />
    </div>
  );
}
